import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { idb } from "@/lib/idb";
import { Camera, Image as ImageIcon, CheckCircle2, Loader2, WifiOff, AlertTriangle, X } from "lucide-react";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function MobileUpload() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [queue, setQueue] = useState([]); // {id, name, dataUrl, status}
  const [online, setOnline] = useState(navigator.onLine);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(null);
  const [doneErr, setDoneErr] = useState("");
  const camRef = useRef(null); const galRef = useRef(null);

  useEffect(() => {
    api.get(`/qr-sessions/${token}`).then((r) => setSession(r.data)).catch((e) => setError(apiErr(e, "Session invalid or expired")));
    (async () => { const saved = (await idb.all("mobile_uploads")) || []; setQueue(saved.filter((s) => s.token === token)); })();
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [token]);

  useEffect(() => {
    if (!online) return;
    const t = setInterval(processQueue, 4000);
    processQueue();
    return () => clearInterval(t);
  }, [online, queue.length]);

  const addFiles = async (files) => {
    for (const file of files) {
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
      const item = { id: uuid(), token, name: file.name, dataUrl, status: "queued" };
      await idb.put("mobile_uploads", item);
      setQueue((q) => [...q, item]);
    }
    if (online) processQueue();
  };

  const processQueue = async () => {
    const items = (await idb.all("mobile_uploads")) || [];
    const pending = items.filter((i) => i.token === token && i.status !== "done");
    for (const item of pending) {
      if (item.status === "uploading") continue;
      item.status = "uploading"; await idb.put("mobile_uploads", item); setQueue((q) => q.map((x) => x.id === item.id ? item : x));
      try {
        const blob = await (await fetch(item.dataUrl)).blob();
        const fd = new FormData(); fd.append("file", blob, item.name || "photo.jpg");
        await api.post(`/qr-sessions/${token}/upload`, fd);
        item.status = "done"; await idb.put("mobile_uploads", item);
        setQueue((q) => q.map((x) => x.id === item.id ? item : x));
      } catch (e) {
        item.status = "queued"; await idb.put("mobile_uploads", item);
        setQueue((q) => q.map((x) => x.id === item.id ? item : x));
        break; // stop; will retry
      }
    }
  };

  const pendingCount = queue.filter((q) => q.status !== "done").length;
  const doneCount = queue.filter((q) => q.status === "done").length;

  const finish = async () => {
    setFinishing(true); setDoneErr("");
    try {
      const { data } = await api.post(`/qr-sessions/${token}/complete`);
      for (const q of queue) if (q.status === "done") await idb.del("mobile_uploads", q.id);
      setFinished({ uploaded: data.uploaded ?? doneCount, product_name: data.product_name || session?.product_name });
    } catch (e) {
      if (e.response?.status === 410) setFinished({ uploaded: doneCount, product_name: session?.product_name });
      else setDoneErr(navigator.onLine ? apiErr(e, "Could not finish — please try again") : "You're offline — reconnect and tap Done again");
    } finally { setFinishing(false); }
  };

  if (finished) return (
    <Center>
      <CheckCircle2 className="text-emerald-400 mb-3" size={56} />
      <div className="font-bold text-xl" data-testid="mobile-finished">All done!</div>
      <p className="text-slate-300 mt-2" data-testid="mobile-finished-count">{finished.uploaded} photo{finished.uploaded === 1 ? "" : "s"} added to <span className="font-semibold">{finished.product_name}</span>.</p>
      <p className="text-slate-400 text-sm mt-3">They're already showing in the POS. You can close this page.</p>
      <p className="text-slate-500 text-xs mt-6">Need more photos? Generate a new QR code from the product on the POS.</p>
    </Center>
  );
  if (error) return <Center><AlertTriangle className="text-amber-400 mb-3" size={40} /><div className="font-semibold">{error}</div><p className="text-slate-400 text-sm mt-1">Please generate a new QR code on the POS.</p></Center>;
  if (!session) return <Center><Loader2 className="animate-spin" /></Center>;

  return (
    <div className="pos-dark min-h-screen">
      <div className="max-w-md mx-auto p-4">
        <div className="ms-card rounded-xl p-4 mb-4">
          <div className="text-xs text-slate-400">Uploading photos for</div>
          <div className="font-bold text-lg" data-testid="mobile-product-name">{session.product_name}</div>
          <div className="text-xs text-slate-500 mono">SKU {session.sku || "—"}</div>
          <div className="mt-2">{online ? <span className="text-xs text-emerald-400">● ONLINE</span> : <span className="text-xs text-amber-400 flex items-center gap-1"><WifiOff size={12} /> OFFLINE — Saved, Waiting to Upload</span>}</div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <button data-testid="mobile-camera" onClick={() => camRef.current.click()} className="touch-btn ms-card ms-card-hover rounded-xl py-6 flex flex-col items-center gap-2"><Camera size={28} className="text-indigo-400" /><span className="text-sm">Take Photo</span></button>
          <button data-testid="mobile-gallery" onClick={() => galRef.current.click()} className="touch-btn ms-card ms-card-hover rounded-xl py-6 flex flex-col items-center gap-2"><ImageIcon size={28} className="text-indigo-400" /><span className="text-sm">From Gallery</span></button>
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => addFiles([...e.target.files])} />
        <input ref={galRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles([...e.target.files])} />

        {pendingCount > 0 && <div className="text-sm text-amber-300 mb-2" data-testid="mobile-pending">{pendingCount} photo(s) waiting to upload</div>}
        <div className="grid grid-cols-3 gap-2">
          {queue.map((q) => (
            <div key={q.id} className="relative aspect-square rounded-lg overflow-hidden border border-slate-700">
              <img src={q.dataUrl} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                {q.status === "done" ? <CheckCircle2 className="text-emerald-400" /> : q.status === "uploading" ? <Loader2 className="animate-spin text-sky-400" /> : <span className="text-[10px] text-amber-300">queued</span>}
              </div>
            </div>
          ))}
        </div>
        {doneErr && <div data-testid="mobile-done-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 mt-4">{doneErr}</div>}
        {queue.length > 0 && (
          <button data-testid="mobile-done" disabled={finishing || pendingCount > 0} onClick={finish}
            className="w-full touch-btn bg-emerald-600 hover:bg-emerald-500 rounded-lg py-3 mt-5 font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {finishing ? <Loader2 className="animate-spin" size={18} /> : null}
            {pendingCount > 0 ? `Uploading ${pendingCount} photo${pendingCount === 1 ? "" : "s"}…` : `Done — ${doneCount} photo${doneCount === 1 ? "" : "s"} uploaded`}
          </button>
        )}
      </div>
    </div>
  );
}

function Center({ children }) { return <div className="pos-dark min-h-screen flex flex-col items-center justify-center p-6 text-center">{children}</div>; }
